import { useState } from "react"
import { router } from "@inertiajs/react"
import { ArrowRight, Plus } from "lucide-react"

import DraftCard from "@/components/tuw/DraftCard"
import DiscardDraftDialog from "@/components/tuw/DiscardDraftDialog"
import { discardDraft } from "@/lib/postProjectActions"

// S-POST-ENTRY — the "Continue a draft / Start new" split, the picker's first
// view when the customer already holds drafts (state E1). Two stacked blocks:
// a "Start a new project" card, then an "Or continue a draft" list of the
// customer's drafts (split-variant cards). Design ref:
// tasks/design-handoff/spec/post/start-project.html (entryView / draftsBlock /
// discardModal).
//
// Props:
//   drafts     — the customer's unposted drafts (shared card payloads)
//   onStartNew — advance into the existing EntryPicker path/select flow
//
// Discard reloads only the drafts + catalog + navCounts props; when the last
// draft goes the controller returns an empty `drafts` and the page collapses to
// the solo picker (state E2).
export default function EntrySplit({ drafts = [], onStartNew }) {
  const [discarding, setDiscarding] = useState(null)

  const confirmDiscard = (draft) => {
    discardDraft(draft.id, {
      onFinish: () => router.reload({ only: ["drafts", "catalog", "navCounts"] }),
    })
    setDiscarding(null)
  }

  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <div className="text-[11px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
          Post a project
        </div>
        <h1 className="font-display text-[22px] font-bold tracking-[-0.4px] text-foreground">
          Pick up where you left off
        </h1>
        <p className="max-w-[560px] text-[13px] leading-relaxed text-muted-foreground text-pretty">
          You have {drafts.length} saved draft{drafts.length > 1 ? "s" : ""}. Resume one below, or
          start something new — nothing is charged until a freelancer is attached.
        </p>
      </div>

      {/* Start a new project (design sf-startnew) — advances into the picker */}
      <button
        type="button"
        onClick={onStartNew}
        className="flex items-center gap-4 rounded-2xl border border-border bg-card px-5 py-[18px] text-left transition-colors hover:border-ink-3"
      >
        <span className="grid size-11 shrink-0 place-items-center rounded-xl border border-primary bg-primary text-primary-foreground">
          <Plus className="size-5" strokeWidth={2.1} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-[15.5px] font-bold tracking-tight text-foreground">
            Start a new project
          </h3>
          <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted-foreground text-pretty">
            Choose a ready-made bundle or build your own — posting is always free.
          </p>
        </div>
        <ArrowRight className="size-[18px] shrink-0 text-muted-foreground" strokeWidth={2} />
      </button>

      {/* Or continue a draft (design sf-or divider + dr-list) */}
      <div className="flex items-center gap-3 text-[11.5px] font-bold uppercase tracking-[0.08em] text-ink-3">
        <span className="h-px flex-1 bg-border" />
        Or continue a draft
        <span className="h-px flex-1 bg-border" />
      </div>

      <div className="flex flex-col gap-2.5">
        {drafts.map((draft) => (
          <DraftCard key={draft.id} draft={draft} variant="split" onDiscard={setDiscarding} />
        ))}
      </div>

      <DiscardDraftDialog
        draft={discarding}
        onOpenChange={() => setDiscarding(null)}
        onConfirm={confirmDiscard}
      />
    </div>
  )
}
